import { Link } from "react-router";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import type { ServiceSummary } from "@veo/types";
import { fetchApi } from "../lib/api";
import { Reveal, RevealItem, RevealList } from "../components/motion";
import { SectionList } from "../blocks/registry";
import { loadOptionalPage, pageMeta } from "../lib/page";
import { seoMeta } from "../lib/seo";
import { loadSectionData } from "../lib/section-data";
import type { Route } from "./+types/services";

export async function loader({ request }: Route.LoaderArgs) {
  const preview = new URL(request.url).searchParams.get("preview");

  // Both are fetched regardless: the services list is what the built-in layout
  // renders, and it is also what a `servicesGrid` section resolves against, so
  // neither path can be skipped on the strength of the other.
  const [services, page] = await Promise.all([
    fetchApi<ServiceSummary[]>("/public/services"),
    loadOptionalPage("/services", preview),
  ]);

  return {
    services,
    page,
    sectionData: page ? await loadSectionData(page.sections) : {},
  };
}

/**
 * The page's own SEO wins where an editor has taken it over in the CMS.
 *
 * The route falls back to a built-in layout when there are no sections, and its
 * metadata should follow the same rule: an editor who has written a title and
 * description for /services in the admin expects to see them in the tab and in
 * a share card, not the constants below.
 */
export function meta({ loaderData, matches }: Route.MetaArgs) {
  if (loaderData?.page) return pageMeta(loaderData.page, { matches });

  return seoMeta({
    matches,
    title: "Services — VEO Consultancy",
    description:
      "Offshore and local stocks advisory, retirement planning and financial coaching for individuals and families in Kenya.",
    path: "/services",
  });
}

function Icon({ name, className }: { name: string | null; className?: string }) {
  const Component =
    (Icons[(name ?? "Circle") as keyof typeof Icons] as typeof Icons.Circle) ?? Icons.Circle;
  return <Component aria-hidden className={className} />;
}

export default function Services({ loaderData }: Route.ComponentProps) {
  const { services, page, sectionData } = loaderData;

  // An editor has taken this page over in the CMS; their layout wins.
  if (page) {
    return (
      <main id="main">
        <SectionList sections={page.sections} data={sectionData} />
      </main>
    );
  }

  return (
    <main id="main">
      <section className="bg-navy-900 textured-dark text-cream-100">
        <div className="container-site section-y">
          <div className="container-content">
            <p className="eyebrow text-gold-300">What we do</p>
            <h1 className="mt-4 text-6xl text-cream-50">Services</h1>
            <p className="mt-6 text-lg leading-relaxed text-navy-100">
              Four advisory services, each built around a different question. Most clients start
              with one and add another as their circumstances change.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-surface-0 textured">
        <div className="container-site section-y">
          {services.length === 0 ? (
            <p className="text-center text-ink-500">
              Our services are being updated. Please check back shortly.
            </p>
          ) : (
            <RevealList className="grid gap-8 md:grid-cols-2">
              {services.map((service) => (
                <RevealItem key={service.id}>
                  <article className="group flex h-full flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-card transition-shadow hover:shadow-lg">
                    {service.cardImageUrl && (
                      <div className="relative aspect-[16/9] overflow-hidden bg-navy-900">
                        <img
                          src={service.cardImageUrl}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none"
                        />
                        {/* Keeps the icon legible over whatever photograph an
                            editor chooses later. */}
                        <div aria-hidden className="absolute inset-0 bg-navy-950/25" />
                      </div>
                    )}

                    <div className="flex flex-1 flex-col p-8">
                      <span className="inline-flex w-fit rounded-md bg-navy-900 p-3 text-gold-300">
                        <Icon name={service.icon} className="size-6" />
                      </span>

                      <h2 className="mt-5 font-display text-2xl text-navy-900">{service.name}</h2>
                      <p className="mt-3 flex-1 text-base leading-relaxed text-ink-600">
                        {service.summary}
                      </p>

                      {service.minInvestment && (
                        <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-gold-700">
                          {service.minInvestment}
                        </p>
                      )}

                      <Link
                        to={`/services/${service.slug}`}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy-700 transition-colors hover:text-navy-900"
                      >
                        Learn more
                        <ArrowRight aria-hidden className="size-4" />
                        <span className="sr-only">about {service.name}</span>
                      </Link>
                    </div>
                  </article>
                </RevealItem>
              ))}
            </RevealList>
          )}
        </div>
      </section>

      <section className="bg-surface-100 textured">
        <div className="container-site section-y">
          <Reveal className="container-content text-center">
            <h2 className="text-4xl">Not sure which fits?</h2>
            <p className="mt-4 text-lg leading-relaxed text-ink-600">
              A first conversation is an hour, costs nothing, and often ends with us suggesting you
              do nothing yet. That is a legitimate answer.
            </p>
            <Link
              to="/book-consultation"
              className="mt-8 inline-block rounded-md bg-navy-900 px-7 py-3.5 text-sm font-semibold text-cream-50 transition-colors hover:bg-navy-800"
            >
              Book a consultation
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
